"use client";
import { Children, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";

import { handleLimitCharacter, imageLoader } from "@aprosoja/utils";
import { AdvertisingBanner, CardNews } from "@aprosoja/containers";
import { Badge, Breadcrumb, Button, Skeleton, Text } from "@aprosoja/ui";

import ShareNewsModal from "@/app/components/share-news-modal/share-news-modal";
import NewsCreationInfo from "./components/news-creation-info";
import useCompleteNews from "./use-complete-news";

interface PageProps {
  params: {
    slug: string;
  };
}

const News = ({ params }: PageProps) => {
  const router = useRouter();
  const { slug } = params;
  const { completeNews, completeNewsIsLoading, newsList, newsListIsLoading } =
    useCompleteNews(slug);

  const [shareNewsModal, setShareNewsModal] = useState(false);

  const segments = [
    { label: "Notícias", path: "noticias" },
    {
      label: `${handleLimitCharacter(completeNews?.title, 50)}`,
      path: `${completeNews?.slug}`,
    },
  ];

  return (
    <>
      {shareNewsModal && (
        <ShareNewsModal
          open
          onOpenChange={setShareNewsModal}
          news={completeNews}
        />
      )}
      <div className="flex flex-col w-full max-w-[1560px] mx-auto gap-size-x14 py-size-x14 px-size-x4 tabletMedium:px-size-x10 ">
        <AdvertisingBanner type="top" />
        <div className="flex flex-col w-full gap-size-x8 ">
          <Breadcrumb segments={segments} />
          <div className="relative flex w-full h-[277px] tabletSmall:w-[650px] tabletLarge:w-[900px] tabletSmall:h-[327px] rounded-border-radius-small tabletMedium:h-[506px] mx-auto">
            {completeNewsIsLoading ? (
              <Skeleton className="bg-neutral-300 w-full h-full" />
            ) : (
              <Image
                loader={imageLoader}
                className="object-cover rounded-border-radius-small"
                src={completeNews?.imageUrl ?? ""}
                alt="imagem-noticia"
                fill
              />
            )}
          </div>
          <div
            className={`flex w-full justify-${completeNews?.tags.length ? "between" : "end"} items-center`}
          >
            {completeNewsIsLoading ? (
              <Skeleton className="bg-neutral-300 w-full h-size-x12" />
            ) : (
              <>
                {!!completeNews?.tags.length && (
                  <div className="flex w-full gap-size-x2">
                    {Children.toArray(
                      completeNews.tags.map((tag) => <Badge>{tag.name}</Badge>)
                    )}
                  </div>
                )}
                <Button
                  className="rounded-border-radius-small"
                  endIcon="share"
                  onClick={() => setShareNewsModal(true)}
                  size="large"
                  variant="outlined"
                >
                  Compartilhar
                </Button>
              </>
            )}
          </div>
          <div className="flex flex-col w-full gap-size-x4">
            {completeNewsIsLoading ? (
              <>
                <Skeleton className="bg-neutral-300 w-full h-size-x8" />
                <Skeleton className="bg-neutral-300 w-full h-size-x30" />
              </>
            ) : (
              <>
                <NewsCreationInfo
                  authorName={completeNews?.author?.name ?? ""}
                  categoryName={completeNews?.category?.name ?? ""}
                  createdAt={completeNews?.createdAt ?? ""}
                />
                <Text type="headingM">{completeNews?.title}</Text>
              </>
            )}
          </div>
          {completeNewsIsLoading ? (
            <Skeleton className="bg-neutral-300 w-full h-[480px]" />
          ) : (
            !!completeNews?.text && (
              <div
                className="content flex flex-col gap-size-x4 px-size-x4 tabletMedium:px-size-x10 desktopMedium:px-size-x30"
                dangerouslySetInnerHTML={{
                  __html: completeNews.text,
                }}
              />
            )
          )}
        </div>
        <div className="flex flex-col w-full gap-size-x10">
          {completeNewsIsLoading ? (
            <Skeleton className="bg-neutral-300 w-full h-[400px]" />
          ) : (
            <>
              <div className="flex w-full items-center justify-between">
                <Text type="headingM">Notícias</Text>
                <Button
                  className="rounded-border-radius-small"
                  variant="text"
                  endIcon="arrow_forward"
                  onClick={() => router.push("/noticias")}
                >
                  Ver mais
                </Button>
              </div>
              <div className="grid grid-cols-1 w-full gap-size-x6 tabletSmall:grid-cols-2 desktopMedium:grid-cols-4 tabletSmall:gap-size-x8">
                {newsListIsLoading
                  ? Children.toArray(
                      [0, 1, 2, 3].map(() => (
                        <Skeleton className="bg-neutral-300 min-w-[328px] h-[289px] tabletSmall:min-w-[346px]" />
                      ))
                    )
                  : Children.toArray(
                      newsList
                        ?.filter((news) => news.slug !== slug)
                        .map((news) => (
                          <div className="w-full">
                            <CardNews hasImage data={news} />
                          </div>
                        ))
                    )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default News;
